import React from 'react';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaArrowRight, FaCheck } from 'react-icons/fa';

export const AssessmentWizard = ({
  currentSection,
  totalSections,
  progress,
  onNext,
  onPrev,
  onSubmit,
  canGoNext,
  canGoPrev,
  isLastSection,
  loading,
  sections
}) => {
  return (
    <div className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm border-b border-gray-700 py-4">
      <div className="max-w-5xl mx-auto px-6">
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-300">
              Step {currentSection + 1} of {totalSections}
            </span>
            <span className="text-sm font-semibold text-teal-400">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
              className="h-full bg-gradient-to-r from-teal-500 to-blue-500 rounded-full"
            />
          </div>
        </div>

        {/* Section Indicators */}
        <div className="hidden md:flex items-center justify-between mb-4">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className="flex items-center"
            >
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all ${
                  index < currentSection
                    ? 'bg-teal-500 border-teal-500 text-white'
                    : index === currentSection
                    ? 'bg-teal-500/20 border-teal-500 text-teal-400'
                    : 'bg-gray-800 border-gray-600 text-gray-500'
                }`}
              >
                {index < currentSection ? (
                  <FaCheck className="text-sm" />
                ) : (
                  <span className="text-xs font-bold">{index + 1}</span>
                )}
              </div>
              {index < sections.length - 1 && (
                <div
                  className={`w-8 h-0.5 mx-1 ${
                    index < currentSection ? 'bg-teal-500' : 'bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-4">
          <motion.button
            type="button"
            onClick={onPrev}
            disabled={!canGoPrev}
            whileHover={canGoPrev ? { scale: 1.02 } : {}}
            whileTap={canGoPrev ? { scale: 0.98 } : {}}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              canGoPrev
                ? 'bg-gray-800 border border-gray-700 text-white hover:border-gray-600'
                : 'bg-gray-800/50 border border-gray-800 text-gray-600 cursor-not-allowed'
            }`}
          >
            <FaArrowLeft className="text-sm" />
            Previous
          </motion.button>

          {isLastSection ? (
            <motion.button
              type="button"
              onClick={onSubmit}
              disabled={loading}
              whileHover={!loading ? { scale: 1.02 } : {}}
              whileTap={!loading ? { scale: 0.98 } : {}}
              className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition-all ${
                loading
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600 shadow-lg'
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Assessment
                  <FaCheck className="text-sm" />
                </>
              )}
            </motion.button>
          ) : (
            <motion.button
              type="button"
              onClick={onNext}
              disabled={!canGoNext}
              whileHover={canGoNext ? { scale: 1.02 } : {}}
              whileTap={canGoNext ? { scale: 0.98 } : {}}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                canGoNext
                  ? 'bg-gradient-to-r from-teal-500 to-blue-500 text-white hover:from-teal-600 hover:to-blue-600 shadow-lg'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
              <FaArrowRight className="text-sm" />
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

